// `Intl.supportedValuesOf` is ES2022; the package's lib is ES2020.
/// <reference lib="es2022.intl" />
/**
 * Wall-clock ↔ UTC conversion in an IANA time zone, shared by the dashboard's
 * backend buckets and the frontend's time ranges so both agree on where a
 * local day, month and year start; and the zone names the pickers offer and
 * the dashboard API accepts.
 */
const DAY_MS = 86400000;
/**
 * The fixed-offset zones `Etc/GMT+1`..`+12` and `Etc/GMT-1`..`-14`, with POSIX
 * signs (`Etc/GMT+5` is UTC−5). Safari's `Intl.supportedValuesOf('timeZone')`
 * lists them; Chrome's and Node's don't, though both accept them. The app
 * offers them on every engine, and the dashboard API accepts them.
 */
export const ETC_GMT_ZONES = [
    ...Array.from({ length: 12 }, (_, i) => `Etc/GMT+${i + 1}`),
    ...Array.from({ length: 14 }, (_, i) => `Etc/GMT-${i + 1}`),
];
// UTC under another name: the IANA aliases, which engines rename to `UTC`
// today, and the zero offset zone (`+00:00`). Folding them here keeps a second
// UTC out of the picker, and every `=== 'UTC'` check right, on any engine.
const UTC_ALIAS = /^((Etc\/)?(UTC|UCT|Universal|Zulu|Greenwich|GMT([+-]?0)?)|[+-]00(:?00)?)$/i;
/**
 * `tz` as `Intl` names it (`asia/tokyo` → `Asia/Tokyo`), with every UTC alias
 * as `UTC`, or null when `Intl` cannot format in it or it is a bare offset
 * (`+05:30`): no picker offers one, and `zonedParts` keeps a formatter per
 * zone for the life of the process, so the keys stay the finite IANA names.
 * Any IANA name passes, under whichever spelling this engine resolves it to,
 * so a saved zone survives an engine that renames it (`Asia/Calcutta` ↔
 * `Asia/Kolkata`).
 */
export function normalizeTimeZone(tz) {
    if (typeof tz !== 'string' || tz === '')
        return null;
    try {
        const zone = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
        }).resolvedOptions().timeZone;
        if (UTC_ALIAS.test(zone))
            return 'UTC';
        return /^[+-]/.test(zone) ? null : zone;
    }
    catch {
        return null;
    }
}
/**
 * Every IANA zone the engine lists, plus `ETC_GMT_ZONES` (only Safari lists
 * them), A→Z. Empty where `Intl.supportedValuesOf` is missing (Safari < 15.4,
 * Chrome < 99) or throws (a partial polyfill).
 */
export function supportedTimeZones() {
    try {
        return [
            ...new Set([...Intl.supportedValuesOf('timeZone'), ...ETC_GMT_ZONES]),
        ].sort();
    }
    catch {
        return [];
    }
}
const partsFormatters = new Map();
/** `date`'s wall clock in `timeZone`. */
export function zonedParts(timeZone, date) {
    let formatter = partsFormatters.get(timeZone);
    if (!formatter) {
        // h23 (not `hour12: false`) avoids the "24:00:00" midnight quirk.
        formatter = new Intl.DateTimeFormat('en-US', {
            timeZone,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
        partsFormatters.set(timeZone, formatter);
    }
    const parts = formatter.formatToParts(date);
    const get = (type) => Number(parts.find(p => p.type === type)?.value ?? '0');
    return {
        year: get('year'),
        month: get('month'),
        day: get('day'),
        hour: get('hour'),
        minute: get('minute'),
        second: get('second'),
    };
}
/** `timeZone`'s offset from UTC (ms) at instant `ms`, from its wall clock. */
function offsetAt(timeZone, ms) {
    const p = zonedParts(timeZone, ms);
    const asIfUtcMs = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    return asIfUtcMs - (ms - (ms % 1000));
}
/**
 * The instant the wall clock in `timeZone` reads `year`-`month`-`day`
 * `hour`:`minute` (month 1-based; out-of-range days and months roll over,
 * like `Date.UTC`). A time a DST change skips moves forward by the gap (02:30
 * on a spring-forward day → 03:30); a time it repeats resolves to the first
 * one.
 */
export function zonedWallTimeToUtc(timeZone, year, month, day, hour = 0, minute = 0) {
    const wall = Date.UTC(year, month - 1, day, hour, minute);
    // The answer lies within 14 h of `wall`, so the offsets a day either side
    // are the ones in force before and after any DST change near it.
    const before = wall - offsetAt(timeZone, wall - DAY_MS);
    const after = wall - offsetAt(timeZone, wall + DAY_MS);
    const reads = (ms) => ms + offsetAt(timeZone, ms) === wall;
    if (reads(before) && reads(after)) {
        // A repeated time (or no change at all): the first one.
        return new Date(Math.min(before, after));
    }
    // Only one reads `wall`, or neither (a skipped time, where `before` is the
    // same wall time on the old offset, i.e. moved forward by the gap).
    return new Date(reads(after) ? after : before);
}
/**
 * Local midnight in `timeZone` on the day `date` falls on there, `daysAgo`
 * calendar days earlier (not 24 h multiples, so DST days count once).
 */
export function localMidnightUtc(timeZone, date, daysAgo = 0) {
    const { year, month, day } = zonedParts(timeZone, date);
    return zonedWallTimeToUtc(timeZone, year, month, day - daysAgo);
}
//# sourceMappingURL=time-zone.js.map